<template>
  <section class="migracao-asaas-page">
    <top-menu/>
    <section>
      <page-header headerName="Migração IUGU → ASAAS" :showBtn="false" btnName="" btnRoute="" css="container-fluid"/>

      <div class="page-content container-fluid">

        <div class="playbook-toggle">
          <button class="btn-secondary" @click="showPlaybook = !showPlaybook">
            {{ showPlaybook ? '▲ Ocultar' : '📖 Como funciona (playbook)' }}
          </button>
        </div>

        <div v-if="showPlaybook" class="playbook">
          <h4>O que é esta tela</h4>
          <p>Central para migrar clientes <strong>mensais recorrentes</strong> da IUGU para o ASAAS, acompanhar quem já foi migrado e corrigir estados inconsistentes. Tudo roda nos 7 shards e é auditado no funil.</p>

          <h4>Status de migração</h4>
          <ul>
            <li><span class="badge badge-iugu">IUGU</span> só tem gateway IUGU — candidato a migrar.</li>
            <li><span class="badge badge-migrado">Migrado</span> tem customer + assinatura no ASAAS.</li>
            <li><span class="badge badge-parcial">Parcial</span> tem customer ASAAS mas sem assinatura — use <strong>Vincular</strong> ou re-execute.</li>
            <li><span class="badge badge-orfao">Órfão</span> BillingTarget=1 sem customer ASAAS (resíduo do kit antigo).</li>
            <li><span class="badge badge-sem_gateway">Sem gateway</span> nem IUGU nem ASAAS.</li>
          </ul>

          <h4>Conta / cenários de gateway</h4>
          <ul>
            <li><strong>IUGU</strong>, <strong>ASAAS-BOH</strong>, <strong>ASAAS-MAM</strong> — a conta/gateway onde a assinatura cobra.</li>
            <li><strong class="txt-danger">IUGU + ASAAS</strong> — a assinatura tem os dois ao mesmo tempo: <strong>risco de cobrança dupla</strong>. Normalmente é migração feita sem suspender a IUGU; use <strong>Suspender IUGU</strong>.</li>
            <li>⚠ <strong>ASAAS-MAM</strong> hoje cai em NF-99 (NFS-e não emitida) — por isso a migração usa conta <strong>BOH</strong> e MARQ fica bloqueada até corrigir o gap fiscal.</li>
          </ul>

          <h4>Ações (em massa, selecionando linhas)</h4>
          <ul>
            <li><strong>Migrar</strong>: <em>Simular (preview)</em> mostra linha a linha o que vai acontecer; depois <em>Executar</em> cria customer+assinatura no ASAAS e suspende a IUGU. Idempotente (não duplica).</li>
            <li><strong>Reajustar valor</strong>: aplica % sobre o valor atual e propaga aos boletos pendentes (updatePendingPayments).</li>
            <li><strong>Suspender IUGU</strong>: para quem ficou com os dois ativos (cobrança dupla).</li>
            <li><strong>Reverter</strong>: cancela boletos ASAAS pendentes, inativa a assinatura ASAAS e reativa a IUGU.</li>
            <li><strong>Importar planilha</strong>: baixe o template (pré-preenchido com os IUGU do filtro), ajuste e suba para conferir antes de executar.</li>
          </ul>

          <h4>Pós-migração</h4>
          <p>A confirmação de pagamento e o desbloqueio passam a vir por <strong>webhook do ASAAS em tempo real</strong>; o job diário (06:00) fica só como reconciliação/healthcheck. Toda ação de criação/edição fica registrada no <strong>funil</strong> e no <strong>histórico de lotes</strong> (abaixo).</p>
        </div>

        <div class="kpi-row">
          <div class="kpi-card active" :class="{ selected: filters.quick === 'mensalAtivo' }" @click="applyQuick('mensalAtivo')">
            <span class="kpi-label">Mensais ativos</span>
            <span class="kpi-value">{{ kpis.mensalAtivo }}</span>
            <span class="kpi-sub">MRR {{ fmtMoney(kpis.mrrMensalAtivo) }}</span>
          </div>
          <div class="kpi-card iugu" :class="{ selected: filters.quick === 'iugu' }" @click="applyQuick('iugu')">
            <span class="kpi-label">Na IUGU (a migrar)</span>
            <span class="kpi-value">{{ kpis.iugu }}</span>
          </div>
          <div class="kpi-card ok" :class="{ selected: filters.quick === 'migrado' }" @click="applyQuick('migrado')">
            <span class="kpi-label">Migrados (ASAAS)</span>
            <span class="kpi-value">{{ kpis.migrado }}</span>
          </div>
          <div class="kpi-card warn" :class="{ selected: filters.quick === 'cartao' }" @click="applyQuick('cartao')">
            <span class="kpi-label">Em cartão</span>
            <span class="kpi-value">{{ kpis.cartao }}</span>
          </div>
          <div class="kpi-card warn" :class="{ selected: filters.quick === 'inadimplente' }" @click="applyQuick('inadimplente')">
            <span class="kpi-label">Inadimplentes</span>
            <span class="kpi-value">{{ kpis.inadimplente }}</span>
          </div>
          <div class="kpi-card danger" :class="{ selected: filters.quick === 'dual' }" @click="applyQuick('dual')">
            <span class="kpi-label">IUGU + ASAAS (duplo)</span>
            <span class="kpi-value">{{ kpis.dual }}</span>
            <span class="kpi-sub">risco de cobrança dupla</span>
          </div>
          <div class="kpi-card danger" :class="{ selected: filters.quick === 'acao' }" @click="applyQuick('acao')">
            <span class="kpi-label">⚠ Requer ação</span>
            <span class="kpi-value">{{ kpis.acao }}</span>
            <span class="kpi-sub">órfão / parcial / mista / NF-99</span>
          </div>
        </div>

        <div class="filters-box">
          <div class="filter">
            <label>Buscar</label>
            <input type="text" v-model="filters.search" placeholder="Empresa ou CNPJ"/>
          </div>
          <div class="filter">
            <label>Periodicidade</label>
            <select v-model="filters.planType">
              <option value="">Todas</option>
              <option value="1">Mensal</option>
              <option value="2">Bimestral</option>
              <option value="3">Trimestral</option>
              <option value="4">Semestral</option>
              <option value="5">Anual</option>
              <option value="6">Bienal</option>
            </select>
          </div>
          <div class="filter">
            <label>Status assinatura</label>
            <select v-model="filters.subStatus">
              <option value="">Todos</option>
              <option value="1">Ativo</option>
              <option value="5">Inadimplente</option>
              <option value="6">Carência</option>
            </select>
          </div>
          <div class="filter">
            <label>Status migração</label>
            <select v-model="filters.migStatus">
              <option value="">Todos</option>
              <option value="IUGU">IUGU</option>
              <option value="MIGRADO">Migrado</option>
              <option value="PARCIAL">Parcial</option>
              <option value="ORFAO">Órfão</option>
              <option value="SEM_GATEWAY">Sem gateway</option>
            </select>
          </div>
          <div class="filter">
            <label>Conta</label>
            <select v-model="filters.account">
              <option value="">Todas</option>
              <option value="IUGU">Só IUGU</option>
              <option value="ASAAS-BOH">ASAAS-BOH</option>
              <option value="ASAAS-MAM">ASAAS-MAM</option>
              <option value="DUAL">IUGU + ASAAS (duplo)</option>
            </select>
          </div>
          <div class="filter">
            <label>Status empresa</label>
            <select v-model="filters.companyStatus">
              <option value="">Todas</option>
              <option value="Ativa">Ativa</option>
              <option value="Inativa">Inativa</option>
              <option value="Deletada">Deletada</option>
            </select>
          </div>
          <div class="filter">
            <label>Método</label>
            <select v-model="filters.paymentMethod">
              <option value="">Todos</option>
              <option value="1">Boleto</option>
              <option value="2">Cartão</option>
            </select>
          </div>
          <div class="filter checkbox">
            <label><input type="checkbox" v-model="filters.onlyRisk"/> Só requer ação</label>
          </div>
          <div class="filter actions">
            <button class="btn-secondary" @click="clearFilters">Limpar</button>
            <button class="btn-secondary" @click="load" :disabled="loading">Atualizar</button>
          </div>
        </div>

        <div class="toolbar">
          <span class="count">
            {{ filteredRows.length }} assinatura(s)
            <template v-if="selected.length"> · <strong>{{ selected.length }} selecionada(s)</strong></template>
          </span>
          <div class="toolbar-right">
            <button v-if="selected.length" class="btn-primary" @click="openMigrar">Migrar selecionadas</button>
            <button v-if="selected.length" class="btn-primary" @click="openReajustar">Reajustar valor</button>
            <button v-if="selected.length" class="btn-primary danger" @click="openSuspender">Suspender IUGU</button>
            <button v-if="selected.length" class="btn-primary danger" @click="openRollback">Reverter</button>
            <button class="btn-secondary" @click="exportCsv" :disabled="!filteredRows.length">Exportar CSV</button>
            <button class="btn-secondary" @click="exportXlsx" :disabled="!filteredRows.length">Exportar XLSX</button>
          </div>
        </div>

        <div class="table-wrap" v-if="!loading">
          <table class="mig-table">
            <thead>
              <tr>
                <th class="sticky-col"><input type="checkbox" :checked="allChecked" @change="toggleAll"/></th>
                <th>Empresa</th>
                <th>CNPJ</th>
                <th>Unidade</th>
                <th>Migração</th>
                <th>Conta</th>
                <th>Assinatura</th>
                <th>Period.</th>
                <th class="num">MRR assin.</th>
                <th class="num">MRR empresa</th>
                <th class="num">Dia venc.</th>
                <th>Método</th>
                <th>Closer</th>
                <th>E-mail financeiro</th>
                <th>NF</th>
                <th class="num">Bol. IUGU pend.</th>
                <th>IDs IUGU</th>
                <th>IDs ASAAS</th>
                <th>Contrato até</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in filteredRows" :key="r.subscriptionId"
                  :class="{ 'row-selected': isSelected(r), 'row-risk': isRisk(r) }">
                <td class="sticky-col">
                  <input type="checkbox" :checked="isSelected(r)" @change="toggleRow(r)"/>
                </td>
                <td>
                  <a :href="masterLink(r)" target="_blank">{{ r.companyName }}</a>
                  <span v-if="r.isMixedCompany" class="tag tag-mista" title="Empresa com assinaturas em gateways diferentes">mista</span>
                  <span v-if="r.companyStatus === 'Deletada'" class="tag tag-fiscal" title="Empresa deletada">deletada</span>
                  <span v-else-if="r.companyStatus === 'Inativa'" class="tag tag-inativa" title="Empresa inativa">inativa</span>
                </td>
                <td>{{ r.effectiveCnpj }}</td>
                <td>{{ r.businessUnitName }}</td>
                <td>
                  <span class="badge" :class="'badge-' + r.migrationStatus.toLowerCase()">{{ statusLabel(r.migrationStatus) }}</span>
                  <span v-if="r.fiscalRiskAsaasMam" class="tag tag-fiscal" title="Boleto ASAAS na conta MAM cai em NF-99 (não emite)">NF-99</span>
                </td>
                <td>
                  <span :class="{ 'txt-danger': r.isDualGateway }" :title="r.isDualGateway ? 'Assinatura com IUGU e ASAAS ao mesmo tempo (risco de cobranca dupla)' : ''">{{ r.gateways }}</span>
                </td>
                <td>
                  <span :class="{ 'txt-danger': r.isInadimplente }">{{ r.subscriptionStatusStr }}</span>
                </td>
                <td>{{ r.planTypeStr }}</td>
                <td class="num">{{ fmtMoney(r.totalCost) }}</td>
                <td class="num">{{ fmtMoney(r.mrrCompany) }}</td>
                <td class="num">{{ r.recurringDayToCharge }}</td>
                <td>
                  <span :class="{ 'txt-danger': r.isCreditCard }">{{ r.paymentMethodStr }}</span>
                </td>
                <td>{{ r.closerName }}</td>
                <td>
                  <span :class="{ 'txt-danger': !r.financialEmail && !r.email }">
                    {{ r.financialEmail || r.email || '— sem e-mail —' }}
                  </span>
                </td>
                <td>{{ r.generateInvoice ? 'Sim' : 'Não' }}</td>
                <td class="num">
                  <span v-if="r.pendingIuguBoletos" class="txt-danger" :title="'R$ ' + r.pendingIuguValue">{{ r.pendingIuguBoletos }}</span>
                  <span v-else>0</span>
                </td>
                <td class="ids">
                  <span v-if="r.iuguSubscriptionId" :title="r.iuguSubscriptionId">assin ✓</span>
                  <span v-if="r.iuguClientId" :title="r.iuguClientId">cli ✓</span>
                  <span v-if="!r.iuguSubscriptionId && !r.iuguClientId">—</span>
                </td>
                <td class="ids">
                  <a v-if="r.asaasSubscriptionId" :href="asaasSubLink(r)" target="_blank" :title="r.asaasSubscriptionId">assin ✓</a>
                  <a v-if="r.asaasCustomerId" :href="asaasCliLink(r)" target="_blank" :title="r.asaasCustomerId">cli ✓</a>
                  <span v-if="!r.asaasSubscriptionId && !r.asaasCustomerId">—</span>
                </td>
                <td>{{ r.contractValidUntil }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="!filteredRows.length" class="empty">Nenhuma assinatura para os filtros atuais.</p>
        </div>
        <div v-else class="loading-box">Carregando assinaturas dos 7 shards...</div>

        <div class="import-box">
          <h4>Importar planilha</h4>
          <p class="hint">Baixe o template (pré-preenchido com os clientes IUGU do filtro atual), ajuste os valores e suba para conferir antes de executar.</p>
          <div class="import-actions">
            <button class="btn-secondary" @click="downloadTemplate">Baixar template (.csv)</button>
            <input type="file" accept=".csv" @change="onUploadFile"/>
            <button class="btn-secondary" :disabled="!uploadFile || uploadRunning" @click="doUpload">
              {{ uploadRunning && uploadPhase === 'upload' ? 'Conferindo...' : 'Subir e conferir' }}
            </button>
          </div>
          <div v-if="uploadPreview" class="import-preview">
            <div class="result-summary">
              <span class="ok">OK: {{ uploadPreview.ok }}</span>
              <span class="skip">Pulado: {{ uploadPreview.pulado }}</span>
              <span class="err">Falha: {{ uploadPreview.falha }}</span>
            </div>
            <table class="result-table">
              <thead><tr><th>Empresa</th><th>Status</th><th>Mensagem</th></tr></thead>
              <tbody>
                <tr v-for="(r, i) in uploadPreview.rows" :key="i" :class="'st-' + (r.status || '').toLowerCase()">
                  <td>{{ r.companyName || r.subscriptionId }}</td>
                  <td>{{ r.status }}</td>
                  <td>{{ r.message }}</td>
                </tr>
              </tbody>
            </table>
            <div class="form-row">
              <label>Senha de operação</label>
              <input type="password" v-model="uploadPassword" placeholder="senha"/>
            </div>
            <button class="btn-primary" :disabled="uploadRunning || !uploadPassword" @click="doExecuteUpload">
              {{ uploadRunning && uploadPhase === 'execute' ? 'Executando...' : 'Executar migração da planilha' }}
            </button>
          </div>
        </div>

        <div class="history-box">
          <h4>Histórico de lotes</h4>
          <table class="result-table" v-if="history.length">
            <thead>
              <tr><th>Quando</th><th>Ação</th><th>Quem</th><th>Total</th><th>OK</th><th>Aviso</th><th>Pulado</th><th>Falha</th></tr>
            </thead>
            <tbody>
              <tr v-for="h in history" :key="h.id">
                <td>{{ fmtDate(h.createdAt) }}</td>
                <td>{{ h.action }}</td>
                <td>{{ h.whoName }}</td>
                <td>{{ h.total }}</td>
                <td class="ok">{{ h.ok }}</td>
                <td class="warn">{{ h.okAviso }}</td>
                <td>{{ h.pulado }}</td>
                <td class="err">{{ h.falha }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="empty">Nenhum lote registrado ainda.</p>
        </div>

        <div class="modal-overlay" v-if="modal.open" @click.self="closeModal">
          <div class="modal-card">
            <div class="modal-head">
              <h4>{{ modalTitle }}</h4>
              <button class="modal-x" @click="closeModal">×</button>
            </div>

            <div class="modal-body">
              <p class="modal-count">{{ modal.items.length }} assinatura(s) selecionada(s).</p>

              <template v-if="modal.mode === 'migrar'">
                <label class="opt"><input type="checkbox" v-model="modal.options.aplicarMultaJuros"/> Aplicar multa (2%) e juros (1%/dia)</label>
                <label class="opt"><input type="checkbox" v-model="modal.options.somenteCustomer"/> Somente criar customer (não criar assinatura)</label>
                <p class="hint">Conta destino: <strong>BOH</strong> (MARQ bloqueada pelo gap fiscal NF-99).</p>
                <p class="hint">O vencimento é buscado na <strong>IUGU</strong> (data real da assinatura) para manter o ciclo; o Simular mostra a data resultante por linha.</p>
              </template>

              <template v-if="modal.mode === 'reajuste'">
                <div class="form-row">
                  <label>Reajuste (%)</label>
                  <input type="number" step="0.01" v-model.number="modal.reajustePct" placeholder="ex.: 8.5"/>
                </div>
                <label class="opt"><input type="checkbox" v-model="modal.updatePending"/> Atualizar também os boletos pendentes (não pagos)</label>
                <p class="hint">Aplica {{ modal.reajustePct || 0 }}% sobre o valor atual de cada assinatura ASAAS selecionada.</p>
              </template>

              <template v-if="modal.mode === 'rollback'">
                <div class="form-row">
                  <label>Motivo da reversão</label>
                  <input type="text" v-model="modal.motive" placeholder="ex.: migração indevida"/>
                </div>
                <p class="hint">Cancela os boletos ASAAS pendentes, inativa a assinatura ASAAS e reativa a assinatura IUGU.</p>
              </template>

              <div class="form-row">
                <label>Senha de operação</label>
                <input type="password" v-model="modal.password" placeholder="senha"/>
              </div>

              <div v-if="modal.result" class="modal-result">
                <div class="result-summary">
                  <span class="ok">OK: {{ modal.result.ok }}</span>
                  <span class="warn">Aviso: {{ modal.result.okComAviso }}</span>
                  <span class="skip">Pulado: {{ modal.result.pulado }}</span>
                  <span class="err">Falha: {{ modal.result.falha }}</span>
                </div>
                <table class="result-table">
                  <thead><tr><th>Empresa</th><th>Status</th><th>Mensagem</th></tr></thead>
                  <tbody>
                    <tr v-for="(r, i) in modal.result.rows" :key="i" :class="'st-' + (r.status || '').toLowerCase()">
                      <td>{{ r.companyName || r.subscriptionId }}</td>
                      <td>{{ r.status }}</td>
                      <td>{{ r.message }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="modal-foot">
              <button class="btn-secondary" @click="closeModal">Fechar</button>
              <template v-if="modal.mode === 'suspender'">
                <button class="btn-primary danger" :disabled="modal.running || !modal.password" @click="doSuspender">
                  {{ modal.running ? 'Suspendendo...' : 'Confirmar suspensão' }}
                </button>
              </template>
              <template v-else-if="modal.mode === 'reajuste'">
                <button class="btn-primary" :disabled="modal.running || !modal.password || !modal.reajustePct" @click="doReajustar">
                  {{ modal.running ? 'Reajustando...' : 'Aplicar reajuste' }}
                </button>
              </template>
              <template v-else-if="modal.mode === 'rollback'">
                <button class="btn-primary danger" :disabled="modal.running || !modal.password" @click="doRollback">
                  {{ modal.running ? 'Revertendo...' : 'Confirmar reversão' }}
                </button>
              </template>
              <template v-else>
                <button class="btn-secondary" :disabled="modal.running" @click="doPreview">
                  {{ modal.running && modal.phase === 'preview' ? 'Simulando...' : 'Simular (preview)' }}
                </button>
                <button class="btn-primary" :disabled="modal.running || !modal.password || !modal.previewed" @click="doExecute">
                  {{ modal.running && modal.phase === 'execute' ? 'Executando...' : 'Executar migração' }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import {
  MIGRACAO_ASAAS_LIST,
  MIGRACAO_ASAAS_PREVIEW,
  MIGRACAO_ASAAS_EXECUTE,
  MIGRACAO_ASAAS_SUSPENDER_IUGU,
  MIGRACAO_ASAAS_ALTERAR,
  MIGRACAO_ASAAS_ROLLBACK,
  MIGRACAO_ASAAS_UPLOAD,
  MIGRACAO_ASAAS_HISTORICO
} from "../../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../../requestConfig";
import { UTIL } from "../../../../../utils";
import topMenu from "../../../components/default/topMenu.vue";
import pageHeader from "../../../components/default/pageHeader.vue";
import swal from "sweetalert";

export default {
  name: "migracaoAsaas",
  components: { topMenu, pageHeader },
  data() {
    return {
      loading: false,
      showPlaybook: false,
      rows: [],
      selected: [],
      filters: {
        search: "",
        planType: "1",
        subStatus: "1",
        migStatus: "",
        account: "",
        companyStatus: "Ativa",
        paymentMethod: "",
        onlyRisk: false,
        quick: ""
      },
      uploadFile: null,
      uploadItems: [],
      uploadPreview: null,
      uploadPassword: "",
      uploadRunning: false,
      uploadPhase: "",
      history: [],
      modal: {
        open: false,
        mode: "migrar",
        items: [],
        password: "",
        running: false,
        phase: "",
        previewed: false,
        result: null,
        reajustePct: 0,
        updatePending: true,
        motive: "",
        options: {
          aplicarMultaJuros: true,
          somenteCustomer: false
        }
      }
    };
  },
  computed: {
    filteredRows() {
      const f = this.filters;
      const search = (f.search || "").toLowerCase().trim();
      return this.rows.filter((r) => {
        if (search) {
          const hay = (r.companyName || "").toLowerCase() + " " + (r.effectiveCnpj || "");
          if (hay.indexOf(search) === -1) return false;
        }
        if (f.planType && String(r.planType) !== f.planType) return false;
        if (f.subStatus && String(r.subscriptionStatus) !== f.subStatus) return false;
        if (f.migStatus && r.migrationStatus !== f.migStatus) return false;
        if (f.account) {
          if (f.account === "IUGU" && !(r.hasIugu && !r.hasAsaas)) return false;
          if (f.account === "ASAAS-BOH" && !(r.hasAsaas && r.billingTarget === 1)) return false;
          if (f.account === "ASAAS-MAM" && !(r.hasAsaas && r.billingTarget === 0)) return false;
          if (f.account === "DUAL" && !r.isDualGateway) return false;
        }
        if (f.companyStatus && r.companyStatus !== f.companyStatus) return false;
        if (f.paymentMethod && String(r.paymentMethod) !== f.paymentMethod) return false;
        if (f.onlyRisk && !this.isRisk(r)) return false;
        return true;
      });
    },
    kpis() {
      const r = this.rows.filter((x) => x.companyStatus === "Ativa");
      const mensalAtivo = r.filter((x) => x.planType === 1 && x.subscriptionStatus === 1);
      return {
        mensalAtivo: mensalAtivo.length,
        mrrMensalAtivo: mensalAtivo.reduce((s, x) => s + (x.totalCost || 0), 0),
        iugu: r.filter((x) => x.migrationStatus === "IUGU").length,
        migrado: r.filter((x) => x.migrationStatus === "MIGRADO").length,
        dual: r.filter((x) => x.isDualGateway).length,
        cartao: r.filter((x) => x.isCreditCard).length,
        inadimplente: r.filter((x) => x.isInadimplente).length,
        acao: r.filter((x) => this.isRisk(x)).length
      };
    },
    allChecked() {
      return this.filteredRows.length > 0 && this.filteredRows.every((r) => this.isSelected(r));
    },
    modalTitle() {
      if (this.modal.mode === "suspender") return "Suspender assinaturas IUGU";
      if (this.modal.mode === "reajuste") return "Reajustar valor das assinaturas ASAAS";
      if (this.modal.mode === "rollback") return "Reverter migração ASAAS";
      return "Migrar para o ASAAS";
    }
  },
  beforeMount() {
    const info = window.localStorage.getItem("mamUserMasterInfo");
    if (!info || UTIL.checkP(JSON.parse(info).id) === false) {
      this.$router.push({ path: "/" });
      return;
    }
    this.load();
    this.loadHistory();
  },
  methods: {
    load() {
      this.loading = true;
      this.selected = [];
      REQUEST_SERVICE.get(MIGRACAO_ASAAS_LIST)
        .then((res) => {
          this.rows = res.data || [];
        })
        .catch(() => {
          swal("Aviso", "Falha ao carregar a lista de migração.", "error");
          this.rows = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isSelected(r) {
      return this.selected.indexOf(r.subscriptionId) !== -1;
    },
    toggleRow(r) {
      const i = this.selected.indexOf(r.subscriptionId);
      if (i === -1) this.selected.push(r.subscriptionId);
      else this.selected.splice(i, 1);
    },
    toggleAll() {
      if (this.allChecked) {
        const visible = this.filteredRows.map((r) => r.subscriptionId);
        this.selected = this.selected.filter((id) => visible.indexOf(id) === -1);
      } else {
        this.filteredRows.forEach((r) => {
          if (this.selected.indexOf(r.subscriptionId) === -1) this.selected.push(r.subscriptionId);
        });
      }
    },
    selectedRows() {
      const set = {};
      this.selected.forEach((id) => { set[id] = true; });
      return this.rows
        .filter((r) => set[r.subscriptionId])
        .map((r) => ({ subscriptionId: r.subscriptionId, companyId: r.companyId, contextCode: r.contextCode, companyName: r.companyName }));
    },
    openMigrar() {
      if (!this.selected.length) return;
      this.modal.items = this.selectedRows();
      this.modal.mode = "migrar";
      this.modal.password = "";
      this.modal.previewed = false;
      this.modal.result = null;
      this.modal.phase = "";
      this.modal.open = true;
    },
    openSuspender() {
      if (!this.selected.length) return;
      this.modal.items = this.selectedRows();
      this.modal.mode = "suspender";
      this.modal.password = "";
      this.modal.previewed = false;
      this.modal.result = null;
      this.modal.phase = "";
      this.modal.open = true;
    },
    openReajustar() {
      if (!this.selected.length) return;
      const set = {};
      this.selected.forEach((id) => { set[id] = true; });
      this.modal.items = this.rows
        .filter((r) => set[r.subscriptionId] && r.migrationStatus === "MIGRADO")
        .map((r) => ({
          subscriptionId: r.subscriptionId,
          companyId: r.companyId,
          contextCode: r.contextCode,
          companyName: r.companyName,
          totalCost: r.totalCost
        }));
      this.modal.mode = "reajuste";
      this.modal.password = "";
      this.modal.result = null;
      this.modal.reajustePct = 0;
      this.modal.updatePending = true;
      this.modal.open = true;
    },
    openRollback() {
      if (!this.selected.length) return;
      this.modal.items = this.selectedRows();
      this.modal.mode = "rollback";
      this.modal.password = "";
      this.modal.motive = "";
      this.modal.result = null;
      this.modal.open = true;
    },
    closeModal() {
      this.modal.open = false;
    },
    migrationPayload(preview) {
      return {
        preview: !!preview,
        operationPassword: this.modal.password,
        options: this.modal.options,
        items: this.modal.items.map((x) => ({
          subscriptionId: x.subscriptionId,
          companyId: x.companyId,
          contextCode: x.contextCode,
          targetAccount: "BOH"
        }))
      };
    },
    doPreview() {
      this.modal.running = true;
      this.modal.phase = "preview";
      REQUEST_SERVICE.post(MIGRACAO_ASAAS_PREVIEW, this.migrationPayload(true))
        .then((r) => { this.modal.result = r.data; this.modal.previewed = true; })
        .catch(() => swal("Aviso", "Falha ao simular a migração.", "error"))
        .finally(() => { this.modal.running = false; this.modal.phase = ""; });
    },
    doExecute() {
      if (!this.modal.password) return;
      this.modal.running = true;
      this.modal.phase = "execute";
      REQUEST_SERVICE.post(MIGRACAO_ASAAS_EXECUTE, this.migrationPayload(false))
        .then((r) => {
          this.modal.result = r.data;
          swal("Concluído", `${r.data.ok} ok, ${r.data.okComAviso} aviso, ${r.data.falha} falha(s).`, r.data.falha ? "warning" : "success");
          this.load();
        })
        .catch((e) => {
          const forbidden = e && e.response && e.response.status === 403;
          swal("Aviso", forbidden ? "Senha de operação inválida." : "Falha ao executar a migração.", "error");
        })
        .finally(() => { this.modal.running = false; this.modal.phase = ""; });
    },
    doSuspender() {
      if (!this.modal.password) return;
      this.modal.running = true;
      const payload = {
        operationPassword: this.modal.password,
        items: this.modal.items.map((x) => ({
          subscriptionId: x.subscriptionId,
          companyId: x.companyId,
          contextCode: x.contextCode
        }))
      };
      REQUEST_SERVICE.post(MIGRACAO_ASAAS_SUSPENDER_IUGU, payload)
        .then((r) => {
          this.modal.result = r.data;
          swal("Concluído", `${r.data.ok} suspensa(s), ${r.data.falha} falha(s).`, r.data.falha ? "warning" : "success");
          this.load();
        })
        .catch((e) => {
          const forbidden = e && e.response && e.response.status === 403;
          swal("Aviso", forbidden ? "Senha de operação inválida." : "Falha ao suspender.", "error");
        })
        .finally(() => { this.modal.running = false; });
    },
    doReajustar() {
      if (!this.modal.password || !this.modal.reajustePct) return;
      this.modal.running = true;
      const pct = Number(this.modal.reajustePct) || 0;
      const payload = {
        operationPassword: this.modal.password,
        updatePendingPayments: this.modal.updatePending,
        items: this.modal.items.map((x) => ({
          subscriptionId: x.subscriptionId,
          companyId: x.companyId,
          contextCode: x.contextCode,
          newValue: Math.round((x.totalCost || 0) * (1 + pct / 100) * 100) / 100
        }))
      };
      REQUEST_SERVICE.post(MIGRACAO_ASAAS_ALTERAR, payload)
        .then((r) => {
          this.modal.result = r.data;
          swal("Concluído", `${r.data.ok} reajustada(s), ${r.data.falha} falha(s).`, r.data.falha ? "warning" : "success");
          this.load();
        })
        .catch((e) => {
          const forbidden = e && e.response && e.response.status === 403;
          swal("Aviso", forbidden ? "Senha de operação inválida." : "Falha ao reajustar.", "error");
        })
        .finally(() => { this.modal.running = false; });
    },
    doRollback() {
      if (!this.modal.password) return;
      this.modal.running = true;
      const payload = {
        operationPassword: this.modal.password,
        motive: this.modal.motive,
        items: this.modal.items.map((x) => ({
          subscriptionId: x.subscriptionId,
          companyId: x.companyId,
          contextCode: x.contextCode
        }))
      };
      REQUEST_SERVICE.post(MIGRACAO_ASAAS_ROLLBACK, payload)
        .then((r) => {
          this.modal.result = r.data;
          swal("Concluído", `${r.data.ok} revertida(s), ${r.data.falha} falha(s).`, r.data.falha ? "warning" : "success");
          this.load();
        })
        .catch((e) => {
          const forbidden = e && e.response && e.response.status === 403;
          swal("Aviso", forbidden ? "Senha de operação inválida." : "Falha ao reverter.", "error");
        })
        .finally(() => { this.modal.running = false; });
    },
    downloadTemplate() {
      const header = "SubscriptionId;ContextCode;CompanyId;CNPJ;Empresa;ValorMensal;DiaVencimento;ContaDestino;CancelarBoletosIuguPendentes;Observacao";
      const rows = this.filteredRows
        .filter((r) => r.migrationStatus === "IUGU")
        .map((r) => [
          r.subscriptionId,
          r.contextCode,
          r.companyId,
          r.effectiveCnpj,
          (r.companyName || "").replace(/;/g, ","),
          Number(r.totalCost || 0).toFixed(2).replace(".", ","),
          r.recurringDayToCharge,
          "BOH",
          "N",
          ""
        ].join(";"));
      const csv = [header].concat(rows).join("\r\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template-migracao-asaas.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    onUploadFile(e) {
      this.uploadFile = e.target.files[0] || null;
      this.uploadPreview = null;
      this.uploadItems = [];
    },
    doUpload() {
      if (!this.uploadFile) return;
      this.uploadRunning = true;
      this.uploadPhase = "upload";
      const fd = new FormData();
      fd.append("file", this.uploadFile);
      REQUEST_SERVICE.requestContext("POST", MIGRACAO_ASAAS_UPLOAD, null, fd, true)
        .then((r) => {
          this.uploadItems = (r.data && r.data.items) || [];
          this.uploadPreview = (r.data && r.data.preview) || null;
        })
        .catch(() => swal("Aviso", "Falha ao conferir a planilha.", "error"))
        .finally(() => { this.uploadRunning = false; this.uploadPhase = ""; });
    },
    doExecuteUpload() {
      if (!this.uploadPassword || !this.uploadItems.length) return;
      this.uploadRunning = true;
      this.uploadPhase = "execute";
      const payload = {
        operationPassword: this.uploadPassword,
        options: { aplicarMultaJuros: true },
        items: this.uploadItems
      };
      REQUEST_SERVICE.post(MIGRACAO_ASAAS_EXECUTE, payload)
        .then((r) => {
          this.uploadPreview = r.data;
          swal("Concluído", `${r.data.ok} ok, ${r.data.falha} falha(s).`, r.data.falha ? "warning" : "success");
          this.load();
          this.loadHistory();
        })
        .catch((e) => {
          const forbidden = e && e.response && e.response.status === 403;
          swal("Aviso", forbidden ? "Senha de operação inválida." : "Falha ao executar.", "error");
        })
        .finally(() => { this.uploadRunning = false; this.uploadPhase = ""; });
    },
    loadHistory() {
      REQUEST_SERVICE.get(MIGRACAO_ASAAS_HISTORICO)
        .then((r) => { this.history = r.data || []; })
        .catch(() => { this.history = []; });
    },
    fmtDate(v) {
      if (!v) return "";
      return new Date(v).toLocaleString("pt-BR");
    },
    applyQuick(kind) {
      if (this.filters.quick === kind) {
        this.filters.quick = "";
        this.clearQuickOnly();
        return;
      }
      this.filters.quick = kind;
      this.filters.migStatus = "";
      this.filters.paymentMethod = "";
      this.filters.onlyRisk = false;
      if (kind === "mensalAtivo") { this.filters.planType = "1"; this.filters.subStatus = "1"; }
      else if (kind === "iugu") { this.filters.migStatus = "IUGU"; this.filters.subStatus = ""; }
      else if (kind === "migrado") { this.filters.migStatus = "MIGRADO"; this.filters.subStatus = ""; }
      else if (kind === "cartao") { this.filters.paymentMethod = "2"; this.filters.subStatus = ""; }
      else if (kind === "inadimplente") { this.filters.subStatus = "5"; }
      else if (kind === "dual") { this.filters.account = "DUAL"; this.filters.subStatus = ""; this.filters.planType = ""; }
      else if (kind === "acao") { this.filters.onlyRisk = true; this.filters.subStatus = ""; this.filters.planType = ""; }
    },
    clearQuickOnly() {
      this.filters.migStatus = "";
      this.filters.paymentMethod = "";
      this.filters.account = "";
      this.filters.onlyRisk = false;
      this.filters.planType = "1";
      this.filters.subStatus = "1";
    },
    clearFilters() {
      this.filters = {
        search: "", planType: "", subStatus: "", migStatus: "",
        account: "", companyStatus: "", paymentMethod: "", onlyRisk: false, quick: ""
      };
    },
    isRisk(r) {
      return r.migrationStatus === "PARCIAL" ||
        r.migrationStatus === "ORFAO" ||
        r.fiscalRiskAsaasMam ||
        r.isDualGateway ||
        r.isMixedCompany ||
        r.billingTarget !== r.companyBillingTarget;
    },
    statusLabel(status) {
      const map = {
        IUGU: "IUGU", MIGRADO: "Migrado", PARCIAL: "Parcial",
        ORFAO: "Órfão", SEM_GATEWAY: "Sem gateway", MISTA: "Mista"
      };
      return map[status] || status;
    },
    masterLink(r) {
      return `https://master.marqponto.com.br/detalhes-da-empresa/${r.companyId}/${r.contextCode}`;
    },
    asaasSubLink(r) {
      return `https://www.asaas.com/subscription/show/${r.asaasSubscriptionId}`;
    },
    asaasCliLink(r) {
      return `https://www.asaas.com/customerAccount/show/${r.asaasCustomerId}`;
    },
    fmtMoney(v) {
      return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    exportColumns() {
      return [
        ["Empresa", (r) => r.companyName],
        ["CNPJ", (r) => r.effectiveCnpj],
        ["Shard", (r) => r.contextCode],
        ["CompanyId", (r) => r.companyId],
        ["SubscriptionId", (r) => r.subscriptionId],
        ["Unidade", (r) => r.businessUnitName],
        ["StatusMigracao", (r) => this.statusLabel(r.migrationStatus)],
        ["Conta", (r) => r.migrationAccount],
        ["StatusAssinatura", (r) => r.subscriptionStatusStr],
        ["Periodicidade", (r) => r.planTypeStr],
        ["MRR_Assinatura", (r) => r.totalCost],
        ["MRR_Empresa", (r) => r.mrrCompany],
        ["DiaVencimento", (r) => r.recurringDayToCharge],
        ["Metodo", (r) => r.paymentMethodStr],
        ["Closer", (r) => r.closerName],
        ["Email", (r) => r.email],
        ["EmailFinanceiro", (r) => r.financialEmail],
        ["EmiteNF", (r) => (r.generateInvoice ? "Sim" : "Nao")],
        ["BoletosIuguPendentes", (r) => r.pendingIuguBoletos],
        ["ValorIuguPendente", (r) => r.pendingIuguValue],
        ["IUGUClientId", (r) => r.iuguClientId],
        ["IUGUSubscriptionId", (r) => r.iuguSubscriptionId],
        ["AsaasCustomerId", (r) => r.asaasCustomerId],
        ["AsaasSubscriptionId", (r) => r.asaasSubscriptionId],
        ["ContaDestino", (r) => (r.billingTarget === 1 ? "BOH" : "MARQ")],
        ["ContratoAte", (r) => r.contractValidUntil]
      ];
    },
    exportXlsx() {
      const cols = this.exportColumns();
      const head = cols.map((c) => c[0]);
      const body = this.filteredRows.map((r) => cols.map((c) => c[1](r)));
      if (window.downloadListExcel) window.downloadListExcel(head, body, "migracao-asaas");
      else this.exportCsv();
    },
    exportCsv() {
      const cols = this.exportColumns();
      const head = cols.map((c) => c[0]).join(";");
      const lines = this.filteredRows.map((r) =>
        cols.map((c) => {
          const v = c[1](r);
          const s = v === null || v === undefined ? "" : String(v);
          return '"' + s.replace(/"/g, '""') + '"';
        }).join(";")
      );
      const csv = [head].concat(lines).join("\r\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "migracao-asaas.csv";
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style lang="scss" scoped>
.migracao-asaas-page {
  .page-content { padding: 20px; }

  .kpi-row {
    display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
    .kpi-card {
      background: #fff; border-radius: 6px; padding: 12px 16px; min-width: 150px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.08); cursor: pointer; border-left: 4px solid #b0bec5;
      display: flex; flex-direction: column; gap: 2px;
      &.selected { outline: 2px solid #1B59F8; }
      &.active { border-left-color: #1B59F8; }
      &.iugu { border-left-color: #ff9800; }
      &.ok { border-left-color: #12B937; }
      &.warn { border-left-color: #f4b400; }
      &.danger { border-left-color: #FF445D; }
      .kpi-label { font-size: 12px; color: #666; }
      .kpi-value { font-size: 22px; font-weight: 700; color: #263238; }
      .kpi-sub { font-size: 11px; color: #999; }
    }
  }

  .filters-box {
    background: #fff; border-radius: 6px; padding: 12px 16px; margin-bottom: 14px;
    display: flex; gap: 14px; flex-wrap: wrap; align-items: flex-end;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    .filter {
      display: flex; flex-direction: column; gap: 3px;
      label { font-size: 11px; color: #777; font-weight: 500; }
      input[type="text"], select {
        border: 1px solid #ccc; border-radius: 4px; padding: 5px 8px; font-size: 13px; min-width: 120px;
      }
      &.checkbox { justify-content: flex-end;
        label { display: flex; align-items: center; gap: 5px; font-size: 13px; color: #444; }
      }
      &.actions { flex-direction: row; gap: 8px; }
    }
  }

  .toolbar {
    display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;
    .count { font-size: 13px; color: #555; }
    .toolbar-right { display: flex; gap: 8px; }
  }

  .btn-secondary {
    background: #fff; border: 1px solid #1B59F8; color: #1B59F8; padding: 6px 14px;
    border-radius: 4px; cursor: pointer; font-size: 13px; font-weight: 600;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .table-wrap { overflow-x: auto; background: #fff; border-radius: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
  .mig-table {
    width: 100%; border-collapse: collapse; font-size: 12px; white-space: nowrap;
    th, td { padding: 7px 10px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
    th { background: #fafafa; color: #555; font-weight: 600; position: sticky; top: 0; }
    th.num, td.num { text-align: right; }
    .sticky-col { position: sticky; left: 0; background: #fff; z-index: 1; }
    th.sticky-col { z-index: 2; background: #fafafa; }
    .row-selected td { background: #eef3ff; }
    .row-selected .sticky-col { background: #eef3ff; }
    .row-risk td { background: #fff8f0; }
    a { color: #1B59F8; text-decoration: none; }
    .ids a { margin-right: 6px; }
    .ids span { margin-right: 6px; color: #999; }
    .txt-danger { color: #FF445D; font-weight: 600; }
  }

  .badge {
    display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; color: #fff;
    &.badge-iugu { background: #ff9800; }
    &.badge-migrado { background: #12B937; }
    &.badge-parcial { background: #f4b400; color: #333; }
    &.badge-orfao { background: #FF445D; }
    &.badge-sem_gateway { background: #90a4ae; }
    &.badge-mista { background: #7e57c2; }
  }
  .tag {
    display: inline-block; margin-left: 5px; padding: 1px 6px; border-radius: 4px; font-size: 10px; font-weight: 600;
    &.tag-mista { background: #ede7f6; color: #5e35b1; }
    &.tag-fiscal { background: #ffebee; color: #c62828; }
    &.tag-inativa { background: #eceff1; color: #607d8b; }
  }

  .playbook-toggle { margin-bottom: 10px; }
  .playbook {
    background: #fff; border-radius: 6px; padding: 16px 20px; margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06); border-left: 4px solid #1B59F8;
    h4 { margin: 14px 0 6px 0; font-weight: bold; color: #263238; font-size: 14px; }
    h4:first-child { margin-top: 0; }
    p { color: #555; font-size: 13px; margin: 4px 0; }
    ul { margin: 4px 0 4px 18px; padding: 0; }
    li { font-size: 13px; color: #555; margin: 3px 0; }
    .badge { margin-right: 4px; }
    .txt-danger { color: #FF445D; }
  }

  .empty, .loading-box { color: #999; text-align: center; padding: 30px 0; }

  .btn-primary {
    background: #1B59F8; color: #fff; border: none; padding: 6px 14px; border-radius: 4px;
    cursor: pointer; font-size: 13px; font-weight: 600;
    &.danger { background: #FF445D; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.45);
    display: flex; align-items: center; justify-content: center; z-index: 1000;
  }
  .modal-card {
    background: #fff; border-radius: 8px; width: 640px; max-width: 92vw; max-height: 88vh;
    display: flex; flex-direction: column; box-shadow: 0 8px 30px rgba(0,0,0,0.2);
  }
  .modal-head {
    display: flex; justify-content: space-between; align-items: center;
    padding: 14px 18px; border-bottom: 1px solid #eee;
    h4 { margin: 0; }
    .modal-x { border: none; background: none; font-size: 22px; cursor: pointer; color: #888; }
  }
  .modal-body {
    padding: 16px 18px; overflow-y: auto;
    .modal-count { font-weight: 600; margin-bottom: 10px; }
    .opt { display: block; font-size: 13px; margin: 6px 0; }
    .form-row { margin: 12px 0;
      label { display: block; font-size: 12px; color: #777; margin-bottom: 3px; }
      input { border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; width: 200px; }
    }
    .hint { font-size: 12px; color: #888; }
  }
  .modal-result {
    margin-top: 14px;
    .result-summary { display: flex; gap: 14px; font-size: 13px; margin-bottom: 8px; font-weight: 600;
      .ok { color: #12B937; } .warn { color: #f4b400; } .skip { color: #888; } .err { color: #FF445D; }
    }
    .result-table { width: 100%; border-collapse: collapse; font-size: 12px;
      th, td { padding: 6px 8px; border-bottom: 1px solid #eee; text-align: left; }
      .st-ok td { background: #f0fbf3; } .st-ok_com_aviso td { background: #fffdf0; }
      .st-falha td { background: #fdf3f3; } .st-pulado td { background: #f6f6f6; }
    }
  }
  .modal-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid #eee; }

  .import-box, .history-box {
    background: #fff; border-radius: 6px; padding: 16px 18px; margin-top: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    h4 { margin: 0 0 6px 0; font-weight: bold; }
    .hint { font-size: 12px; color: #888; }
    .import-actions { display: flex; gap: 10px; align-items: center; margin: 10px 0; flex-wrap: wrap; }
    .import-preview { margin-top: 12px; }
    .form-row { margin: 10px 0;
      label { display: block; font-size: 12px; color: #777; margin-bottom: 3px; }
      input { border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; width: 200px; }
    }
  }
  .result-summary { display: flex; gap: 14px; font-size: 13px; margin-bottom: 8px; font-weight: 600;
    .ok { color: #12B937; } .warn { color: #f4b400; } .skip { color: #888; } .err { color: #FF445D; }
  }
  .result-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 10px;
    th, td { padding: 6px 8px; border-bottom: 1px solid #eee; text-align: left; }
    th { color: #555; font-weight: 600; background: #fafafa; }
    .st-ok td { background: #f0fbf3; } .st-ok_com_aviso td { background: #fffdf0; }
    .st-falha td { background: #fdf3f3; } .st-pulado td { background: #f6f6f6; }
    .ok { color: #12B937; } .warn { color: #f4b400; } .err { color: #FF445D; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/payment/migracaoAsaas/migracaoAsaas.vue